import { useCallback, useEffect, useRef, useState } from "react";
import { APIFieldItem } from "@/pages/settings/types";
import { AutofillPromptOwner, autofillPromptOwnerFor } from "../const";
import { useDeleteAutofillDraftQuestion } from "./useAutofillApi";

/**
 * Renaming an out-of-the-box workflow field replaces it rather than editing it in place:
 * the edited content becomes a new admin-authored field and the original is retired from
 * the workflow, so it stays available to be added again.
 */
export interface SystemFieldReplacement {
  /** The draft question carrying the new prompt, or null when autofill is off. */
  draftQuestionId: string | null;
}

/** A conversion draft, stored with the field it was created for. */
interface ConversionDraft {
  fieldId: string;
  questionId: string;
}

interface UseSystemFieldReplacementResult {
  promptOwner: AutofillPromptOwner;
  /** Set from the autofill section's draft; null unless a replacement is under way. */
  onAutofillQuestionIdChange: (questionId: string | null) => void;
  /** Goes on the save payload — null when the field is being edited in place. */
  replacement: SystemFieldReplacement | null;
  /** Delete the draft the rename created, for a drawer closing without saving. */
  discardDraft: () => void;
  /** Called once a save has been handed off, so the published draft isn't deleted. */
  markSaved: () => void;
}

/**
 * The rule for editing an out-of-the-box workflow field, shared by both edit drawers so
 * they cannot drift on how a rename is detected.
 *
 * `currentLabel` is whatever the drawer's form holds. An empty one counts as unchanged:
 * both drawers copy the field into their form a render late, and a field cannot be saved
 * without a name anyway.
 */
export const useSystemFieldReplacement = (
  fieldData: APIFieldItem | null,
  currentLabel: string
): UseSystemFieldReplacementResult => {
  const fieldId = fieldData?.id ?? null;
  const [draft, setDraft] = useState<ConversionDraft | null>(null);
  const deleteDraft = useDeleteAutofillDraftQuestion();

  // Only a connector-filled field is replaced on rename. An out-of-the-box field without a
  // connector already runs on its own prompt, so a rename edits it in place.
  const hasAutofillConnector =
    fieldData?.hasAutofillConnector ?? fieldData?.isSystem ?? false;
  const trimmedLabel = currentLabel.trim();
  const isRenamed =
    hasAutofillConnector &&
    !!trimmedLabel &&
    trimmedLabel !== (fieldData?.label ?? "").trim();
  const promptOwner = autofillPromptOwnerFor(hasAutofillConnector, isRenamed);

  // Mutation objects are new on every render, so this is held by ref rather than listed as
  // a dependency of the callbacks below — written after commit so render stays pure.
  const deleteDraftRef = useRef(deleteDraft);
  useEffect(() => {
    deleteDraftRef.current = deleteDraft;
  });
  const savedRef = useRef(false);

  const draftForCurrentField = draft?.fieldId === fieldId ? draft : null;
  // The drawer is not modal, so selecting another row swaps the field in place — without
  // unmounting and without the close handler running. A draft left behind by the field
  // previously being edited must never be published as this one's replacement.
  const draftForPreviousField =
    draft && draft.fieldId !== fieldId ? draft : null;

  useEffect(() => {
    if (!draftForPreviousField) {
      return;
    }
    // Same gate as discardDraft: a handed-off save may already have published this draft
    // as a live question (with answers migrated onto it), so switching rows after a failed
    // save must leak it, not delete it.
    if (!savedRef.current) {
      deleteDraftRef.current.mutate({
        pathParams: { questionId: draftForPreviousField.questionId }
      });
    }
    setDraft(null);
    savedRef.current = false;
  }, [draftForPreviousField]);

  const onAutofillQuestionIdChange = useCallback(
    (questionId: string | null) => {
      // The autofill section reports the field's own question when nothing was renamed.
      // Only a conversion draft — a new question created for the edited name — is ours to
      // manage; recording anything else would point the cleanup at a real field.
      if (!questionId || !isRenamed || !fieldId || questionId === fieldId) {
        setDraft(null);
        return;
      }
      setDraft({ fieldId, questionId });
      // A fresh draft is unsaved by definition — without this, a draft created after a
      // completed save would inherit the saved mark and survive an unsaved close.
      savedRef.current = false;
    },
    [fieldId, isRenamed]
  );

  const markSaved = useCallback(() => {
    savedRef.current = true;
  }, []);

  const discardDraft = useCallback(() => {
    if (draftForCurrentField && !savedRef.current) {
      deleteDraftRef.current.mutate({
        pathParams: { questionId: draftForCurrentField.questionId }
      });
    }
    setDraft(null);
    savedRef.current = false;
  }, [draftForCurrentField]);

  return {
    promptOwner,
    onAutofillQuestionIdChange,
    replacement: isRenamed
      ? { draftQuestionId: draftForCurrentField?.questionId ?? null }
      : null,
    discardDraft,
    markSaved
  };
};
